import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRocket,
  faArrowRight,
  faCheck,
  faClock,
  faShieldAlt,
  faUsers,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const CTA: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  // @ts-ignore
  const { t } = useTranslation('landing');

  const guarantees = [
    {
      icon: faShieldAlt,
      title: t('cta.guarantees.reliable.title'),
      description: t('cta.guarantees.reliable.description')
    },
    {
      icon: faUsers,
      title: t('cta.guarantees.support.title'),
      description: t('cta.guarantees.support.description')
    },
    {
      icon: faChartLine,
      title: t('cta.guarantees.guaranteed.title'),
      description: t('cta.guarantees.guaranteed.description')
    }
  ];

  const handleRegisterClick = () => {
    // Scroll to the form section
    return navigate(`/${lang}/register`);
  }

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-800 via-gray-800/80 to-purple-900/30 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>

      {/* Floating Elements */}
      <div className="absolute top-10 left-10 w-24 h-24 sm:w-32 sm:h-32 bg-purple-500/10 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-10 right-10 w-32 h-32 sm:w-40 sm:h-40 bg-blue-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      <div className="absolute top-1/2 left-1/4 w-20 h-20 sm:w-24 sm:h-24 bg-pink-500/10 rounded-full blur-2xl animate-float" style={{ animationDelay: '1s' }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center">
          {/* Main CTA */}
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-5xl mx-auto">

            {/* Headline */}
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight px-4">
              {t('cta.title')}
              <span className="block bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                {t('cta.titleHighlight')}
              </span>
              {t('cta.titleEnd') && <span className="block">{t('cta.titleEnd')}</span>}
            </h2>

            {/* Subtitle */}
            <p
              className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/70 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed px-4"
            // style={{
            //   orphans: 4,
            //   widows: 4,
            //   textWrap: 'pretty'
            // }}
            >
              {t('cta.subtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-8 sm:mb-12">
              <button
                onClick={handleRegisterClick}
                className="w-full sm:w-auto group bg-gradient-to-r from-purple-500 to-purple-700 px-8 sm:px-12 md:px-20 py-3 sm:py-4 md:py-5 rounded-xl sm:rounded-2xl text-white font-bold text-base sm:text-lg md:text-xl md:hover:from-purple-600 md:hover:to-purple-800 transition-all duration-300 transform md:hover:scale-105 md:active:scale-95 md:hover:shadow-2xl md:hover:shadow-purple-500/25 touch-manipulation"
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  userSelect: 'none'
                }}
              >
                <span className="flex items-center space-x-3">
                  <span>{t('cta.getStarted')}</span>
                </span>
              </button>

              {/* <div className="text-white/60 text-sm">
                <span className="font-semibold">14 gün ücretsiz</span> • Kredi kartı gerekmez
              </div> */}
            </div>

            {/* Guarantees */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {guarantees.map((guarantee, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 sm:p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-400/20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={guarantee.icon} className="text-green-400 text-sm sm:text-base" />
                  </div>
                  <div className="w-full">
                    <div className="text-white font-semibold text-xs sm:text-sm">
                      {guarantee.title}
                    </div>
                    <div className="text-white/60 text-xs">
                      {guarantee.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-8 text-white/60 text-xs sm:text-sm">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faChartLine} className="text-blue-400" />
                <span>{t('cta.trustIndicators.averageGrowth')}</span>
              </div>
            </div>
          </div>

          {/* Urgency Message */}
          {/* <div className="mt-8 backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 max-w-2xl mx-auto">
            <div className="flex items-center justify-center space-x-3 text-white/80">
              <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse"></div>
              <span className="text-sm">
                <span className="font-semibold">Sınırlı Süre:</span> Yeni üyelere özel %50 indirim!
              </span>
            </div>
          </div> */}
        </div>
      </div>
    </section>
  );
};

export default CTA;
